import {
  ActionsMenuAction,
  ActionsMenuButton,
  BaseTab,
  BaseTabPanel,
  BaseTabs,
  colors,
  Modal,
  Stack,
  Table,
  TableContainer,
  TabPanelContainer,
  Typography,
  useTabState,
} from '@ui-components/neo.tax';
import { ReactElement, useEffect, useMemo, useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import {
  ConnectProvidersModal,
  FileUploadTicketingProvider,
  LockedRegion,
  ProviderType,
  TicketingConnectionAction,
  TicketingConnectionRow,
  TicketingConnections,
  TicketingEntityType,
  useDisabledProviders,
  usePageLocked,
  useTicketingConnectModal,
  CONNECT_SLIDE_OVER_ACTIONS,
  useCurrentCompany,
  useCurrentUserScopeForCurrentCompany,
  useUserFromAuth,
  bulkTicketingFileUploadToDataSourcesTableItem,
  githubEmailMappingUploadToDataSourcesTableItem,
  syncedPayrollMonthExpenseToDataSourcesTableItem,
  timesheetUploadToDataSourcesTableItem,
  DataSourcesTableItem,
  dataSourceTableCellClassNames,
  DeleteFiscalYearProvider,
  DeleteTicketingFileUpload,
  GithubEmailMappingSection,
  GithubEmailMappingUploadModal,
  useGithubEmailMappingData,
  OneSchemaModal,
  OneSchemaTimesheetModal,
  TicketingFileUploadModal,
  DataSourceSection,
  payrollTokenToDataSourcesTableItem,
} from '@neo.tax/shared';

import { AscCostUploadModal } from '../asc-cost-upload-modal';
import { AscLockPageLayout } from '../asc-lock-page-layout';
import { DeletePayrollMonthExpenseSyncEvent } from '../delete-payroll-month-expense-sync-event';
import { useAscDataSourcesTableColumns, useAscDataSourceLayoutAnalyticIds } from './hooks';

const TAB_IDS = {
  cost: 'asc-data-sources-tab-cost',
  ticketing: 'asc-data-sources-tab-ticketing',
};

const PERIOD_CLOSED_HINT = 'This period is closed.';

/**
 * Its own component so the period lock can be read from inside the page's lock provider, which the
 * layout below only renders — the uploads write into the selected period and close with it, while
 * the company-level connections beside them stay usable.
 */
const TicketingTabPanel = ({
  companyId,
  onAddConnection,
  onConnectionAction,
  onUploadFiles,
  onUploadGithubEmails,
  readOnly,
  uploadFilesBtnId,
  uploads,
}: {
  companyId: string;
  onAddConnection: () => void;
  onConnectionAction: (action: TicketingConnectionAction, connection: TicketingConnectionRow) => void;
  onUploadFiles: () => void;
  readOnly: boolean;
  uploadFilesBtnId: string;
  onUploadGithubEmails?: () => void;
  uploads?: ReactElement;
}): ReactElement => {
  const locked = usePageLocked();
  const periodActions: ActionsMenuAction[] = [
    { label: 'Upload ticketing files', onSelect: onUploadFiles, id: uploadFilesBtnId },
    ...(onUploadGithubEmails ? [{ label: 'Upload GitHub emails', onSelect: onUploadGithubEmails }] : []),
  ].map((action) => ({ ...action, disabled: locked, hint: PERIOD_CLOSED_HINT }));

  return (
    <Stack as="section" aria-label="Ticketing" direction="column" gap={3} fullWidth className="shrink-0">
      <TicketingConnections
        companyId={companyId}
        hasImportedData={!!uploads}
        onAddConnection={onAddConnection}
        readOnly={readOnly}
        slideOver={{ actions: CONNECT_SLIDE_OVER_ACTIONS, onAction: onConnectionAction }}
        additionalActions={periodActions}
      />
      {uploads ? <LockedRegion>{uploads}</LockedRegion> : <></>}
    </Stack>
  );
};

export const AscDataSourcesLayout = (): React.ReactElement => {
  const { company } = useCurrentCompany();
  const { fiscalYear } = useAscContext();
  const { uploadAscCostBtnId, uploadTicketingProviderBtnId } = useAscDataSourceLayoutAnalyticIds();
  const {
    shouldPromptUpload: shouldPromptGithubEmailUpload,
    shouldShowUploadButton: shouldShowGithubEmailUploadButton,
    uploads: githubEmailMappingUploads,
  } = useGithubEmailMappingData({
    companyId: company?.id ?? '',
    syncedTicketingProviders: company?.syncedTicketingProviders ?? [],
  });
  const [isOpenGithubEmailMappingModal, setIsOpenGithubEmailMappingModal] = useState<boolean>(false);

  const [isOpenTicketingFileUploadModal, setIsOpenTicketingFileUploadModal] = useState<boolean>(false);
  const [isOpenOneSchemaModal, setIsOpenOneSchemaModal] = useState<boolean>(false);
  const [isOpenOneSchemaTimesheetModal, setIsOpenOneSchemaTimesheetModal] = useState<boolean>(false);
  const [isOpenAscCostUploadModal, setIsOpenAscCostUploadModal] = useState<boolean>(false);
  const tabState = useTabState({ defaultSelectedId: TAB_IDS.cost });
  const [oneSchemaParameters, setOneSchemaParameters] = useState<{
    oneSchemaJwt?: string;
    permanentUrl?: string;
    signedUrl?: string;
    ticketingEntityType?: TicketingEntityType;
    ticketingProvider?: FileUploadTicketingProvider;
  }>({});

  // Cost sources hold financial data, so only users with financial access change them; non-financial
  // users may also manage ticketing. Sections they cannot change render as a read-only list.
  const { isNonFinancial, limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const canModifyFinancialSources = !limitAccessToFinancialData;
  const canModifyTicketingSources = canModifyFinancialSources || isNonFinancial;

  const {
    close: closeConnectProvidersModal,
    connectModalState,
    initialGitHubConnectMethod,
    initialOrganizationSlug,
    initialProvider,
    openForConnectionAction,
    openForTypes,
  } = useTicketingConnectModal();

  // A ticketing connect session belongs to the Ticketing tab — including one reopened by a GitHub App
  // install return, which lands on this page with the tab state at its default. That return reopens
  // the slide-over from the URL alone, so the permission gates the open state, not just the buttons.
  const offersFinancialProviders = Array.from(connectModalState.showProvidersInTypes).some(
    (providerType) => providerType !== ProviderType.Ticketing,
  );
  const isConnectOpen =
    connectModalState.isOpen && (offersFinancialProviders ? canModifyFinancialSources : canModifyTicketingSources);
  const isTicketingConnectOpen = isConnectOpen && connectModalState.showProvidersInTypes.has(ProviderType.Ticketing);
  const { setSelectedId } = tabState;
  useEffect(() => {
    if (isTicketingConnectOpen) {
      setSelectedId(TAB_IDS.ticketing);
    }
  }, [isTicketingConnectOpen, setSelectedId]);
  const openTicketingModal = (): void => openForTypes([ProviderType.Ticketing]);

  const disabledProviders = useDisabledProviders({
    disableConditions: { accounting: 'sync', payroll: 'sync' },
    fiscalYearId: fiscalYear.id,
  });

  const [itemToDelete, setItemToDelete] = useState<DataSourcesTableItem>();
  const connectionTableColumns = useAscDataSourcesTableColumns({
    canModify: canModifyFinancialSources,
    setItemToDelete,
  });
  const payrollFileUploadTableColumns = useAscDataSourcesTableColumns({
    canModify: canModifyFinancialSources,
    setItemToDelete,
    tableType: 'payroll-file-upload',
  });
  const ticketingFileUploadTableColumns = useAscDataSourcesTableColumns({
    canModify: canModifyTicketingSources,
    setItemToDelete,
    tableType: 'ticketing-file-upload',
  });
  const { isSuperUserMode } = useUserFromAuth();

  const onCancelToggleModal = (): void => {
    setItemToDelete(undefined);
  };

  const tableData = useMemo(
    () =>
      company
        ? {
            ticketingUploads: [
              ...company.bulkTicketingDataUploads.map(bulkTicketingFileUploadToDataSourcesTableItem),
              ...githubEmailMappingUploads.map(githubEmailMappingUploadToDataSourcesTableItem),
            ],
            payrollConnections: (company.payrollTokens ?? []).map(payrollTokenToDataSourcesTableItem),
            payrollUploads: [
              ...company.timesheetUploads.map(timesheetUploadToDataSourcesTableItem),
              ...company.syncedPayrollMonthExpense.map(syncedPayrollMonthExpenseToDataSourcesTableItem),
            ],
          }
        : { ticketingUploads: [], payrollConnections: [], payrollUploads: [] },
    [company, githubEmailMappingUploads],
  );

  if (!company) {
    return <></>;
  }

  const renderSources = (
    data: DataSourcesTableItem[],
    emptyMessage: string,
    columns = connectionTableColumns,
  ): React.ReactElement => (
    <TableContainer>
      <Table<DataSourcesTableItem>
        columns={columns}
        data={data}
        customCellClassNames={[dataSourceTableCellClassNames]}
        autoHeight
        zeroState={
          <Typography fontSize="xs" color={colors.gray[600]}>
            {emptyMessage}
          </Typography>
        }
      />
    </TableContainer>
  );

  const renderSourceGroup = (
    connections: DataSourcesTableItem[],
    uploads: DataSourcesTableItem[],
    emptyMessage: string,
    uploadColumns: typeof connectionTableColumns,
  ): React.ReactElement => (
    <>
      {connections.length > 0 && renderSources(connections, '', connectionTableColumns)}
      {uploads.length > 0 && renderSources(uploads, '', uploadColumns)}
      {connections.length === 0 && uploads.length === 0 && renderSources([], emptyMessage)}
    </>
  );

  const costActions: ActionsMenuAction[] = [
    ...(isSuperUserMode
      ? [{ label: 'Upload timesheet files', onSelect: (): void => setIsOpenOneSchemaTimesheetModal(true) }]
      : []),
    {
      label: 'Upload ASC 350-40 cost',
      onSelect: (): void => setIsOpenAscCostUploadModal(true),
      id: uploadAscCostBtnId,
    },
  ];

  return (
    <AscLockPageLayout
      title="Data Sources"
      subtitle="Import cost or ticketing data."
      fullWidth
      pageHeaderBorder={false}
      // This page's lock covers a period's data, and ticketing connections are company-level: they
      // read the same whichever year is selected, and an ASC-only company has no other page to
      // manage them from. So the page closes its own regions and the connections stay usable where
      // they are, inside the Ticketing tab, rather than the whole page going inert.
      lockScope="regions"
    >
      <GithubEmailMappingSection
        isOpeningUploader={isOpenGithubEmailMappingModal}
        isVisible={canModifyTicketingSources && shouldPromptGithubEmailUpload}
        onUpload={(): void => setIsOpenGithubEmailMappingModal(true)}
      />
      <Stack direction="column" fullWidth className="shrink-0">
        <BaseTabs state={tabState}>
          <BaseTab label="Cost" tabId={TAB_IDS.cost} />
          <BaseTab label="Ticketing" tabId={TAB_IDS.ticketing} />
        </BaseTabs>
        <TabPanelContainer>
          <BaseTabPanel state={tabState} tabId={TAB_IDS.cost}>
            <LockedRegion>
              <DataSourceSection
                title="Cost"
                actions={
                  canModifyFinancialSources ? (
                    <ActionsMenuButton label="Add cost data" collapseSingleAction actions={costActions} />
                  ) : undefined
                }
              >
                {renderSourceGroup(
                  tableData.payrollConnections,
                  tableData.payrollUploads,
                  'No cost connections or imported files.',
                  payrollFileUploadTableColumns,
                )}
              </DataSourceSection>
            </LockedRegion>
          </BaseTabPanel>

          <BaseTabPanel state={tabState} tabId={TAB_IDS.ticketing}>
            <TicketingTabPanel
              companyId={company.id}
              onAddConnection={openTicketingModal}
              onConnectionAction={openForConnectionAction}
              onUploadFiles={(): void => setIsOpenTicketingFileUploadModal(true)}
              readOnly={!canModifyTicketingSources}
              uploadFilesBtnId={uploadTicketingProviderBtnId}
              onUploadGithubEmails={
                shouldShowGithubEmailUploadButton ? (): void => setIsOpenGithubEmailMappingModal(true) : undefined
              }
              uploads={
                tableData.ticketingUploads.length > 0
                  ? renderSources(tableData.ticketingUploads, '', ticketingFileUploadTableColumns)
                  : undefined
              }
            />
          </BaseTabPanel>
        </TabPanelContainer>
      </Stack>

      <ConnectProvidersModal
        fiscalYearId={fiscalYear.id}
        {...disabledProviders}
        {...connectModalState}
        isOpen={isConnectOpen}
        initialProvider={initialProvider}
        initialGitHubConnectMethod={initialGitHubConnectMethod}
        initialOrganizationSlug={initialOrganizationSlug}
        close={closeConnectProvidersModal}
      />
      <TicketingFileUploadModal
        close={(): void => setIsOpenTicketingFileUploadModal(false)}
        showOneSchema={({
          oneSchemaJwt,
          permanentUrl,
          signedUrl,
          ticketingEntityType,
          ticketingProvider,
        }: {
          oneSchemaJwt: string;
          permanentUrl: string;
          signedUrl: string;
          ticketingEntityType: TicketingEntityType;
          ticketingProvider: FileUploadTicketingProvider;
        }): void => {
          setOneSchemaParameters({
            oneSchemaJwt,
            permanentUrl,
            signedUrl,
            ticketingEntityType,
            ticketingProvider,
          });
          setIsOpenOneSchemaModal(true);
        }}
        isOpen={isOpenTicketingFileUploadModal}
      />
      <OneSchemaModal
        close={(): void => setIsOpenOneSchemaModal(false)}
        companyId={company.id}
        isOpen={isOpenOneSchemaModal}
        onLaunched={(): void => {
          setIsOpenTicketingFileUploadModal(false);
        }}
        {...oneSchemaParameters}
      />
      <OneSchemaTimesheetModal
        close={(): void => setIsOpenOneSchemaTimesheetModal(false)}
        companyId={company.id}
        isOpen={isOpenOneSchemaTimesheetModal}
      />
      <AscCostUploadModal
        close={(): void => setIsOpenAscCostUploadModal(false)}
        companyId={company.id}
        fiscalYearId={fiscalYear.id}
        isOpen={isOpenAscCostUploadModal}
      />
      <GithubEmailMappingUploadModal
        close={(): void => setIsOpenGithubEmailMappingModal(false)}
        companyId={company.id}
        isOpen={isOpenGithubEmailMappingModal}
      />

      <Modal isOpen={!!itemToDelete} setIsOpen={onCancelToggleModal}>
        {itemToDelete?.itemType === 'ticketing-file' ? (
          <DeleteTicketingFileUpload close={onCancelToggleModal} itemToDelete={itemToDelete} />
        ) : itemToDelete?.itemType === 'asc-cost' || itemToDelete?.itemType === 'payroll-month-expense' ? (
          <DeletePayrollMonthExpenseSyncEvent close={onCancelToggleModal} itemToDelete={itemToDelete} />
        ) : (
          <DeleteFiscalYearProvider
            close={onCancelToggleModal}
            fiscalYearId={fiscalYear.id}
            accountTableItem={itemToDelete}
          />
        )}
      </Modal>
    </AscLockPageLayout>
  );
};
